import type { Booking } from '../../data/bookings';

/**
 * Which bookings the Home bar is about, and in what order.
 *
 * -----------------------------------------------------------------------------
 * THE RATING CARD RIDES WITH THE LIVE ONES - IT IS NOT A FALLBACK
 * -----------------------------------------------------------------------------
 * This used to read "live bookings if there are any, otherwise the job that
 * just finished". So a customer with one job completed and another still
 * running - three services booked for one morning is the ordinary case - was
 * never asked about the one that had just ended. The question waited behind
 * every other booking they had, and by the time the last of those closed the
 * twenty-four hours were usually up.
 *
 * Both now, and the finished one FIRST. It is the only card that wants
 * something from the customer this minute; the live ones are status, and are
 * one swipe behind it.
 *
 * -----------------------------------------------------------------------------
 * ONE RATING CARD AT A TIME
 * -----------------------------------------------------------------------------
 * The most recently completed unrated job, and only that one. A row of rating
 * cards is a chore rather than a question; rating this one brings the next
 * forward.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Furthest along first: the one with a code on it is the one to land on. */
const STAGE_RANK: Record<string, number> = {
  service: 4,
  onway: 3,
  assigned: 2,
  booked: 1,
};

export type BarOptions = {
  /** Booking ids the customer has rated in this session, not yet on the row. */
  ratedIds?: ReadonlySet<string> | Record<string, unknown>;
  /** The one prompt put away with the cross, for this session. */
  hiddenId?: string | null;
  /** Injected so the 24-hour window is testable. */
  now?: number;
};

const has = (
  ids: BarOptions['ratedIds'],
  id: string,
): boolean => {
  if (!ids) return false;
  return ids instanceof Set ? ids.has(id) : id in (ids as object);
};

export function pickBarBookings(
  bookings: readonly Booking[],
  { ratedIds, hiddenId = null, now = Date.now() }: BarOptions = {},
): Booking[] {
  /*
   * An unpaid booking is not live in any sense a customer would recognise -
   * nothing has been dispatched - so it is left out; the cart and the booking
   * screen already say what is owed.
   */
  const live = bookings
    .filter(item => item.status === 'live' && !item.awaitingPayment)
    .sort((a, b) => (STAGE_RANK[b.stage] ?? 0) - (STAGE_RANK[a.stage] ?? 0));

  /*
   * Finished within the day, never rated, not dismissed.
   *
   * By `completedAt`, not by position: the list is ordered by when a booking
   * was MADE, and the job that just ended is routinely not the newest one.
   */
  const since = now - DAY_MS;
  let unrated: Booking | undefined;
  let unratedAt = 0;
  for (const item of bookings) {
    if (item.status !== 'completed') continue;
    if (item.id === hiddenId || item.review || has(ratedIds, item.id)) continue;
    if (!item.completedAt) continue;

    const at = Date.parse(item.completedAt);
    if (Number.isNaN(at) || at <= since) continue;
    if (at > unratedAt) {
      unrated = item;
      unratedAt = at;
    }
  }

  return unrated ? [unrated, ...live] : live;
}
